"use client";

import React, { useCallback } from "react";
import { ChromePicker, ColorResult } from "react-color";
import styled, { useTheme } from "styled-components";
import { Editor } from "@tiptap/core";

import PopupMenu from "../../PopupMenu";
import Box from "../../Box";
import { getColor } from "../../utils";
import useStoredState from "../../../hooks/useStoredState";

const StyledColorPicker = styled(ChromePicker)`
  width: 100% !important;
`;

const RECENT_COLOR_COUNT = 3;

type Props = {
  editor: Editor;
};

const HighlightColorOptions: React.FC<Props> = ({ editor }) => {
  const theme = useTheme();

  const infoColor =
    theme?.active && theme.noteInfoColor ? theme.noteInfoColor : getColor("noteInfo");
  const warningColor =
    theme?.active && theme.noteWarningColor ? theme.noteWarningColor : getColor("noteWarning");
  const successColor =
    theme?.active && theme.noteSuccessColor ? theme.noteSuccessColor : getColor("noteSuccess");
  const neutralColor =
    theme?.active && theme.noteNeutralColor ? theme.noteNeutralColor : getColor("noteNeutral");

  const activeColor = (editor.getAttributes("highlight")?.color as string) || null;
  const pickerColor = activeColor?.startsWith("#") ? activeColor : "#ff0000";

  const [recentColors, setRecentColors] = useStoredState<string[]>("highlight-recent-colors", []);

  const applyHighlight = useCallback(
    (color: string) => {
      editor.chain().focus().setHighlight({ color }).run();
    },
    [editor]
  );

  const removeHighlight = useCallback(() => {
    editor.chain().focus().unsetHighlight().run();
  }, [editor]);

  const onCustomColorChange = useCallback(
    (color: ColorResult) => {
      applyHighlight(color.hex);
      setRecentColors(prev => {
        const upper = color.hex.toUpperCase();
        const newColors = [upper, ...prev.filter(c => c !== upper)];
        return newColors.slice(0, RECENT_COLOR_COUNT);
      });
    },
    [applyHighlight, setRecentColors]
  );

  return (
    <>
      <PopupMenu.Section title="Presets">
        <PopupMenu.Item
          key="none"
          title="None"
          onClick={removeHighlight}
          checked={!activeColor}
          image={{ color: "transparent" }}
        />
        <PopupMenu.Item
          key="info"
          title="Info"
          onClick={() => applyHighlight(infoColor)}
          checked={activeColor === infoColor}
          image={{ color: infoColor }}
        />
        <PopupMenu.Item
          key="warning"
          title="Warning"
          onClick={() => applyHighlight(warningColor)}
          checked={activeColor === warningColor}
          image={{ color: warningColor }}
        />
        <PopupMenu.Item
          key="success"
          title="Success"
          onClick={() => applyHighlight(successColor)}
          checked={activeColor === successColor}
          image={{ color: successColor }}
        />
        <PopupMenu.Item
          key="neutral"
          title="Neutral"
          onClick={() => applyHighlight(neutralColor)}
          checked={activeColor === neutralColor}
          image={{ color: neutralColor }}
        />
      </PopupMenu.Section>
      <PopupMenu.Section title={recentColors.length > 0 ? "Recent colors" : null}>
        {recentColors.length > 0 && (
          <>
            {recentColors.map(color => (
              <PopupMenu.Item
                key={color}
                title={color}
                onClick={() => applyHighlight(color)}
                checked={activeColor === color}
                image={{ color }}
              />
            ))}
          </>
        )}
        <PopupMenu.Item
          key="custom"
          title="Custom"
          image={{ iconId: "content.custom-colors", fill: "", size: "16" }}
          checked={
            activeColor?.startsWith("#") &&
            ![infoColor, warningColor, successColor, neutralColor, ...recentColors].includes(
              activeColor
            )
          }>
          <Box onClick={e => e.stopPropagation()}>
            <StyledColorPicker
              color={pickerColor}
              onChangeComplete={onCustomColorChange}
              disableAlpha
            />
          </Box>
        </PopupMenu.Item>
      </PopupMenu.Section>
    </>
  );
};

export default HighlightColorOptions;
